import { FeatureFlag, FeatureToggleService } from '@/features/feature-flags';
import CountryCode from '@/features/location/types/CountryCode';
import { Instance as CompanyService } from '@/legacy/services/company';
import { Instance as UserService } from '@/legacy/services/user';

const REFERRALS_BANNER_DISMISSED_KEY = 'ReferralsBannerDismissed';

/** Gets the locally stored dismissed user ids, as a fallback/cache for the BE. */
const getDismissedUserIds = () => {
  let dismissedUserIds: Array<string>;
  try {
    dismissedUserIds = JSON.parse(localStorage.getItem(REFERRALS_BANNER_DISMISSED_KEY));
  } catch {
    // do nothing
  }
  return Array.isArray(dismissedUserIds) ? dismissedUserIds : [];
};

/** Returns true if Referrals is available for the current company. */
export const isReferralsAvailableForCurrentCompany = async () => {
  const isLaunched = await FeatureToggleService.isFeatureEnabledForCurrentCompany(
    FeatureFlag.SPR23_2023_REFERRALS
  );
  const isSubscriber = Boolean(CompanyService.profile()?.isOnActivePlootoMonthlySubscriptionPlan());
  const country = CompanyService.profile()?.country();
  const state = CompanyService.profile()?.state();

  const isInEligibleRegion =
    // TX is excluded by virtue of not being allowed to sign up
    country === CountryCode.UnitedStates ||
    // QC is being excluded by legal requirements
    (country === CountryCode.Canada && state !== 'QC');

  return isLaunched && isSubscriber && isInEligibleRegion;
};

/** Returns true if the ReferralsBanner is dismissed. */
export const isReferralsBannerDismissed = () =>
  // TODO(PLP-32642): Consult BE when API exists to retain this state.
  getDismissedUserIds().includes(UserService.UserId());

/** Returns true if the ReferralsBanner is visible and not dismissed. */
export const isReferralsBannerVisible = async () => {
  const isReferralsAvailable = await isReferralsAvailableForCurrentCompany();
  if (!isReferralsAvailable) {
    return false;
  }
  return !isReferralsBannerDismissed();
};

/** Saves the dismiss state for the ReferralsBanner for the current user. */
export const dismissReferralsBanner = async () => {
  // TODO(PLP-32642): Consult BE when API exists to retain this state.
  const dismissedUserIds = getDismissedUserIds();
  try {
    localStorage.setItem(
      REFERRALS_BANNER_DISMISSED_KEY,
      JSON.stringify(Array.from(new Set(dismissedUserIds).add(UserService.UserId())))
    );
  } catch {
    // do nothing
  }
};
